import { useQuery } from 'data-fetching-client';
import { useMemo } from 'react';
import { useProductDetailsForArray } from './useProductDetails';
import { isValidProductLevel } from '../utils/typeguards';
const orderedProductLevels = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];
import { GET_PRODUCT_INVENTORY_QUERY } from '../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { useSetOneGlobalLoadingState } from '../../context/GlobalLoadingStateContext';
import { useIsInAppAuth } from './useUserInfo';
export const useProductInventory = ({
  skip = false,
  onCompleted
} = {}) => {
  const isInApp = useIsInAppAuth();
  const onCompletedCallback = useSetOneGlobalLoadingState('ProductInventory', onCompleted);
  const {
    data,
    loading,
    error
  } = useQuery(GET_PRODUCT_INVENTORY_QUERY, {
    onCompleted: onCompletedCallback,
    skip: skip || !isInApp
  });
  const productInventory = data === null || data === void 0 ? void 0 : data.result;
  const products = productInventory === null || productInventory === void 0 ? void 0 : productInventory.products;
  const crmSuiteLevel = productInventory === null || productInventory === void 0 ? void 0 : productInventory.crmSuiteLevel;
  return {
    products,
    crmSuiteLevel,
    loading,
    error
  };
};
export const useHasApiNameInProductInventory = ({
  apiName
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  const hasProductInInventory = products === null || products === void 0 ? void 0 : products.some(product => product.productApiName === apiName);
  return {
    hasProductInInventory,
    loading,
    error
  };
};
export const useHasSkuIdInProductInventory = ({
  skuId
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  const hasSkuIdInInventory = products === null || products === void 0 ? void 0 : products.some(product => product.skuId === skuId);
  return {
    hasSkuIdInInventory,
    loading,
    error
  };
};
export const useIsFreePortal = () => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  return {
    isFreePortal: !(products !== null && products !== void 0 && products.length),
    loading,
    error
  };
};
export const useIsPaidPortal = () => {
  const {
    products,
    loading,
    error
  } = useProductInventory();
  return {
    isPaidPortal: !!(products !== null && products !== void 0 && products.length),
    loading,
    error
  };
};
export const useProductFromProductInventoryByApiName = ({
  apiName
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory({
    skip: !apiName
  });
  const matchingProduct = useMemo(() => {
    return products === null || products === void 0 ? void 0 : products.find(product => product.productApiName === apiName);
  }, [products, apiName]);
  return {
    product: matchingProduct,
    loading,
    error
  };
};
export const useProductFromProductInventoryBySkuId = ({
  skuId
}) => {
  const {
    products,
    loading,
    error
  } = useProductInventory({
    skip: !skuId
  });
  const matchingProduct = useMemo(() => {
    return products === null || products === void 0 ? void 0 : products.find(product => product.skuId === skuId);
  }, [products, skuId]);
  return {
    product: matchingProduct,
    loading,
    error
  };
};
export const useProductInventoryQuantityBySkuId = ({
  skuId
}) => {
  var _product$skuQuantityP;
  const {
    product,
    loading,
    error
  } = useProductFromProductInventoryBySkuId({
    skuId
  });
  return {
    quantity: (_product$skuQuantityP = product === null || product === void 0 ? void 0 : product.skuQuantityPurchased) !== null && _product$skuQuantityP !== void 0 ? _product$skuQuantityP : 0,
    loading,
    error
  };
};
export const useProductInventoryQuantityByApiName = ({
  apiName
}) => {
  var _product$skuQuantityP2;
  const {
    product,
    loading,
    error
  } = useProductFromProductInventoryByApiName({
    apiName
  });
  return {
    quantity: (_product$skuQuantityP2 = product === null || product === void 0 ? void 0 : product.skuQuantityPurchased) !== null && _product$skuQuantityP2 !== void 0 ? _product$skuQuantityP2 : 0,
    loading,
    error
  };
};
const calculateHighestProductLevel = productLevels => {
  if (productLevels.length === 0) {
    return 'FREE';
  }
  const maxIndex = Math.max(0, ...productLevels.map(level => orderedProductLevels.indexOf(level)));
  return orderedProductLevels[maxIndex];
};
export const useHighestProductLevelFromInventory = () => {
  const {
    products,
    loading: inventoryLoading,
    error: inventoryError
  } = useProductInventory();
  const productApiNames = useMemo(() => {
    if (!(products !== null && products !== void 0 && products.length)) return [];
    return products.map(product => product.productApiName);
  }, [products]);
  const {
    productDetails,
    loading: detailsLoading
  } = useProductDetailsForArray({
    productApiNames
  });
  const highestProductLevel = useMemo(() => {
    if (inventoryLoading || detailsLoading) {
      return null;
    }
    if (!(products !== null && products !== void 0 && products.length)) {
      return 'FREE';
    }
    if (!(productDetails !== null && productDetails !== void 0 && productDetails.productsByName)) {
      return null;
    }
    const productLevels = products.map(inventoryProduct => {
      const details = productDetails.productsByName[inventoryProduct.productApiName];
      return details === null || details === void 0 ? void 0 : details.productLevel;
    }).filter(isValidProductLevel);
    return calculateHighestProductLevel(productLevels);
  }, [products, productDetails, inventoryLoading, detailsLoading]);
  const loading = inventoryLoading || detailsLoading;
  const error = inventoryError;
  return {
    highestProductLevel,
    loading,
    error
  };
};